/**
 * Ator do Tracker de Combate ligado a um token da cena (BRAZ-24, docs/spec/table-media.md): o ator
 * de um token invisível some do combate para quem não vê o token. O ator guarda a cena e o token
 * (`sceneId`, `tokenId`); quem lê o ator segue o que vale para o token (`tokenReadPerms`). O
 * servidor refaz essas permissões quando o token ou o ator mudam; o mestre não as grava à mão.
 */
import { NDB_GROUPS } from './library.js';
import { tokenReadPerms, type TokenReadPerm, type TokenReadState } from './sceneInvisible.js';

/** O tipo de dado do Tracker de Combate (a tabela embutida da sala). */
export const COMBAT_TRACKER = 'rpgdesk:combatTracker';
/** Atributos do ator com a cena e o token a que ele está ligado. */
export const ACTOR_SCENE_ATTR = 'sceneId';
export const ACTOR_TOKEN_ATTR = 'tokenId';

export type ActorVisibility = 'public' | 'protected' | 'private';

/**
 * As entradas de leitura do nó do ator. Privado, só os mestres o leem, mesmo ligado. Ligado a um
 * token invisível, valem as do token: só quem controla o token e os liberados (o Ocultar do token
 * deixa só os mestres). Fora isso, todos leem. `token` é o estado do token ligado (null: sem
 * ligação, ou o token não existe mais); `readers`, quem já tem entrada de leitura no nó.
 */
export function actorReadPerms(vis: ActorVisibility, token: TokenReadState | null, readers: readonly string[]): TokenReadPerm[] {
  if (vis !== 'private' && token?.invisible) return tokenReadPerms(token, readers);
  return [{ kind: 'group', id: NDB_GROUPS.everyone, allowance: vis === 'private' ? 'deny' : null }, ...[...new Set(readers)].map((id): TokenReadPerm => ({ kind: 'user', id, allowance: null }))];
}

type PermEntry = { kind: string; id: string; permission: string; allowance: string };

/** Só as entradas que mudam o que o nó tem hoje (`current`, as permissões dele). */
export const changedReadPerms = (wanted: readonly TokenReadPerm[], current: readonly PermEntry[]): TokenReadPerm[] =>
  wanted.filter((p) => (current.find((c) => c.kind === p.kind && c.id === p.id && c.permission === 'read')?.allowance ?? null) !== p.allowance);
